import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { BaseService } from '../base/base.service';
import { SessionUserHashManagerService } from '../session-user-hash-manager/session-user-hash-manager.service';
import {
  LeapStaffRole, OrbitRoleMapHistoryEntry, OrbitRoleMapItem, OrbitRoleMapUpdateRequest, StaffSyncReport
} from '../../_models/admin/staff-sync.models';

/**
 * Typed calls to the admin-only Orbit staff sync endpoints: the last run's report and the Orbit role →
 * LEAP role mapping (list, save, history, and the LEAP staff roles to choose from).
 *
 * Same BaseService pattern as FeatureSwitchService: the object passed to prepareOptions() must be the
 * exact object sent as the body (GETs pass null), and every response is unwrapped from observe:'response'.
 */
@Injectable({ providedIn: 'root' })
export class StaffSyncService extends BaseService {

  private apiUrl: string = environment.apiUrl;

  constructor(private http: HttpClient, protected hashManagerService: SessionUserHashManagerService) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  public getReport(): Observable<StaffSyncReport> {
    return this.http.get(this.apiUrl + 'api/Maintenance/StaffSync', this.prepareOptions(null)).pipe(
      map((data: any) => this.unwrap(data) as StaffSyncReport),
      catchError(this.handleError('getStaffSyncReport'))
    );
  }

  public getRoleMap(): Observable<OrbitRoleMapItem[]> {
    return this.http.get(this.apiUrl + 'api/Maintenance/OrbitRoleMap', this.prepareOptions(null)).pipe(
      map((data: any) => (this.unwrap(data) || []) as OrbitRoleMapItem[]),
      catchError(this.handleError('getOrbitRoleMap'))
    );
  }

  /** Replaces one Orbit role's mapping (several TM roles, or none for no access) and returns the row as it now stands. */
  public saveRoleMap(orbitRoleName: string, leapRoleCodes: string[]): Observable<OrbitRoleMapItem> {
    const body: OrbitRoleMapUpdateRequest = { leapRoleCodes: leapRoleCodes || [] };
    return this.http.put(
      this.apiUrl + 'api/Maintenance/OrbitRoleMap/' + encodeURIComponent(orbitRoleName),
      body,
      this.prepareOptions(body)
    ).pipe(
      map((data: any) => this.unwrap(data) as OrbitRoleMapItem),
      catchError(this.handleError('saveOrbitRoleMap'))
    );
  }

  /** The latest 50 changes to one mapping, newest first. */
  public getRoleMapHistory(orbitRoleName: string): Observable<OrbitRoleMapHistoryEntry[]> {
    return this.http.get(
      this.apiUrl + 'api/Maintenance/OrbitRoleMap/' + encodeURIComponent(orbitRoleName) + '/history',
      this.prepareOptions(null)
    ).pipe(
      map((data: any) => (this.unwrap(data) || []) as OrbitRoleMapHistoryEntry[]),
      catchError(this.handleError('getOrbitRoleMapHistory'))
    );
  }

  public getLeapStaffRoles(): Observable<LeapStaffRole[]> {
    return this.http.get(this.apiUrl + 'api/Maintenance/LeapStaffRoles', this.prepareOptions(null)).pipe(
      map((data: any) => (this.unwrap(data) || []) as LeapStaffRole[]),
      catchError(this.handleError('getLeapStaffRoles'))
    );
  }

  private unwrap(data: any): any {
    return data ? data.body : data;
  }
}
